import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, Form, Button, Row, Col, Badge, Modal, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { FaTimes, FaPlus, FaTrash, FaCog, FaCopy, FaGripVertical } from 'react-icons/fa';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RunbookSystemDropdown from '../runbook/RunbookSystemDropdown';
import SystemIcon from '../runbookSystem/SystemIcon';
import WorkflowAutocompleteWithExpand from './WorkflowAutocompleteWithExpand';
import { getSystemObjectSuggestions } from './systemObjectSuggestions';
import { punchOutTemplates, punchOutRequestTypes } from './punchOutTemplates';
import { parsePunchOutSample } from './punchOutSampleParser';
import PixelWizard from './PixelWizard';
import { runbookService } from '../../services/runbook.service';

// Field mappings this panel itself sets (a fixed name/checkbox elsewhere in the form, not
// an entry the user adds/removes/reorders here) — kept out of the draggable "Field Mappings"
// list. Order among THESE is left untouched by drag-reorder; only the user-visible entries
// reorder among themselves (see reorderFieldMappings below).
const SYSTEM_MANAGED_FIELD_NAMES = [
  'FixedLengths',
  'SkipLines',
  'IncludeFormattedValues',
  'ToAddress',
  'Subject',
  'MessageBody',
  'AttachmentText',
  'ValueToLog',
  'ErrorMessage',
];
const isSystemManagedField = (mapping) => SYSTEM_MANAGED_FIELD_NAMES.includes(mapping.name);

// Wraps one field-mapping Card with dnd-kit's sortable wiring. The drag handle only
// activates dragging (attributes/listeners are scoped to it, not the whole card) so
// clicking into the Name/Value inputs still works normally. Hidden entirely when there's
// nothing to reorder against (one item, or fewer) or the panel is read-only.
function SortableFieldMappingCard({ id, dragEnabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="d-flex mb-2">
      {dragEnabled && (
        <div
          {...attributes}
          {...listeners}
          className="d-flex align-items-center border border-end-0 rounded-start bg-light text-muted"
          style={{ cursor: 'grab', padding: '0 6px' }}
          title="Drag to reorder"
        >
          <FaGripVertical size={12} />
        </div>
      )}
      <Card className={`border flex-grow-1${dragEnabled ? ' rounded-start-0' : ''}`}>
        <Card.Body className="p-2">{children}</Card.Body>
      </Card>
    </div>
  );
}

function StepConfigPanel({ node, onUpdate, onClose, onConfigChange, connectors, runbookVariables, clientVariables, availableRunbooks = [], allNodes, edges, readOnly = false, onCopyFieldMappings, runbookContext, onScaffoldFromPreview }) {
  const runbookSystemReducer = useSelector((state) => state.runbookSystem);
  const [config, setConfig] = useState({
    label: node?.data?.label || '',
    description: node?.data?.description || '',
    connectorId: node?.data?.connectorId || '',
    connectorNumber: node?.data?.connectorNumber || '',
    systemObject: node?.data?.systemObject || '',
    system: node?.data?.system || '',
    fieldsToQuery: node?.data?.fieldsToQuery || '',
    filter: node?.data?.filter || '',
    condition: node?.data?.condition || '',
    fieldMappings: node?.data?.fieldMappings || [],
    customErrorMessage: node?.data?.customErrorMessage || '',
    netChange: node?.data?.netChange || false,
    dataStepId: node?.data?.dataStepId || '',
    offsetRecordProcessing: node?.data?.offsetRecordProcessing || false,
    offset: node?.data?.offset || 0,
    debugMode: node?.data?.debugMode || false,
    debugStartingAt: node?.data?.debugStartingAt || 0,
    debugNumberOfRecords: node?.data?.debugNumberOfRecords || 0,
    setVarType: node?.data?.setVarType || '',
    setVarValue: node?.data?.setVarValue || '',
    conditionTruePath: node?.data?.conditionTruePath || '',
    conditionFalsePath: node?.data?.conditionFalsePath || '',
    inspectRequest: node?.data?.inspectRequest || false,
    serialProcessing: node?.data?.serialProcessing || false,
    errorUponCompletion: node?.data?.errorUponCompletion || false,
    targetRunbookId: node?.data?.targetRunbookId || '',
    targetRunbookGuid: node?.data?.targetRunbookGuid || '',
    nestedItems: node?.data?.nestedItems || false,
  });

  // Store original config to restore on cancel
  const [originalConfig, setOriginalConfig] = useState(null);
  const [currentNodeId, setCurrentNodeId] = useState(null);

  // System Object autocomplete state
  const [showSystemObjectSuggestions, setShowSystemObjectSuggestions] = useState(false);
  const [systemObjectSuggestions, setSystemObjectSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const systemObjectInputRef = useRef(null);

  // Copy field mappings modal state
  const [showCopyFieldMappingsModal, setShowCopyFieldMappingsModal] = useState(false);

  // PunchOut template modal state
  const [showPunchOutTemplateModal, setShowPunchOutTemplateModal] = useState(false);
  const [punchOutRequestType, setPunchOutRequestType] = useState('');
  const [punchOutTemplateValues, setPunchOutTemplateValues] = useState({});
  const [punchOutSampleText, setPunchOutSampleText] = useState('');
  const [punchOutParseFeedback, setPunchOutParseFeedback] = useState(null);

  // Preview pushed data modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [pushDataUrlCopied, setPushDataUrlCopied] = useState(false);

  // Scaffold state — populated by parsing previewResult
  const [scaffoldStructure, setScaffoldStructure] = useState(null); // { flatFields, nestedArrays }
  const [scaffoldSelections, setScaffoldSelections] = useState(new Set());

  useEffect(() => {
    if (node) {
      const nodeConfig = {
        label: node.data.label || '',
        description: node.data.description || '',
        connectorId: node.data.connectorId || '',
        connectorNumber: node.data.connectorNumber || '',
        systemObject: node.data.systemObject || '',
        system: node.data.system || '',
        fieldsToQuery: node.data.fieldsToQuery || '',
        filter: node.data.filter || '',
        condition: node.data.condition || '',
        fieldMappings: node.data.fieldMappings || [],
        customErrorMessage: node.data.customErrorMessage || '',
        netChange: node.data.netChange || false,
        dataStepId: node.data.dataStepId || '',
        offsetRecordProcessing: node.data.offsetRecordProcessing || false,
        offset: node.data.offset || 0,
        inspectRequest: node.data.inspectRequest || false,
        serialProcessing: node.data.serialProcessing || false,
        errorUponCompletion: node.data.errorUponCompletion || false,
        debugMode: node.data.debugMode || false,
        debugStartingAt: node.data.debugStartingAt || 0,
        debugNumberOfRecords: node.data.debugNumberOfRecords || 0,
        setVarType: node.data.setVarType || '',
        setVarValue: node.data.setVarValue || '',
        conditionTruePath: node.data.conditionTruePath || '',
        conditionFalsePath: node.data.conditionFalsePath || '',
        // For executeRunbook, try to get from dedicated fields first, then fall back to fieldMappings
        targetRunbookId: node.data.targetRunbookId ||
          node.data.fieldMappings?.find(m => m.name === 'runbookid')?.value || '',
        targetRunbookGuid: node.data.targetRunbookGuid ||
          node.data.fieldMappings?.find(m => m.name === 'runbookguid')?.value || '',
        nestedItems: node.data.nestedItems || false,
      };
      setConfig(nodeConfig);

      // Only store original config if this is a NEW node (not an update to the same node)
      if (node.id !== currentNodeId) {
        const originalCopy = JSON.parse(JSON.stringify(nodeConfig));
        setOriginalConfig(originalCopy);
        setCurrentNodeId(node.id);
      }
    }
  }, [node, currentNodeId, originalConfig?.fieldMappings]);

  // Notify parent of config changes for auto-save
  useEffect(() => {
    if (onConfigChange) {
      onConfigChange(config);
    }
  }, [config, onConfigChange]);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // System Object autocomplete handlers
  const handleSystemObjectChange = (e) => {
    const value = e.target.value;
    handleChange('systemObject', value);

    // Get suggestions based on selected system
    const suggestions = getSystemObjectSuggestions(config.system);
    if (suggestions.length > 0) {
      const filtered = suggestions.filter(s =>
        s.object.toLowerCase().includes(value.toLowerCase())
      );
      setSystemObjectSuggestions(filtered);
      setShowSystemObjectSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(0);
    } else {
      setShowSystemObjectSuggestions(false);
    }
  };

  const handleSystemObjectFocus = () => {
    console.log('System Object Focus - config.system:', config.system);
    const suggestions = getSystemObjectSuggestions(config.system);
    console.log('Suggestions found:', suggestions);
    if (suggestions.length > 0) {
      const filtered = config.systemObject
        ? suggestions.filter(s => s.object.toLowerCase().includes(config.systemObject.toLowerCase()))
        : suggestions;
      setSystemObjectSuggestions(filtered);
      setShowSystemObjectSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(0);
    }
  };

  const handleSystemObjectBlur = () => {
    // Delay hiding to allow click on suggestion
    setTimeout(() => setShowSystemObjectSuggestions(false), 200);
  };

  const handleSystemObjectKeyDown = (e) => {
    if (!showSystemObjectSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          (prev + 1) % systemObjectSuggestions.length
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          (prev - 1 + systemObjectSuggestions.length) % systemObjectSuggestions.length
        );
        break;
      case 'Enter':
      case 'Tab':
        if (systemObjectSuggestions.length > 0) {
          e.preventDefault();
          selectSystemObjectSuggestion(systemObjectSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSystemObjectSuggestions(false);
        break;
      default:
        break;
    }
  };

  const selectSystemObjectSuggestion = (suggestion) => {
    handleChange('systemObject', suggestion.object);
    setShowSystemObjectSuggestions(false);
    if (systemObjectInputRef.current) {
      systemObjectInputRef.current.focus();
    }
  };

  const addFieldMapping = () => {
    setConfig(prev => ({
      ...prev,
      fieldMappings: [...prev.fieldMappings, { name: '', value: '' }]
    }));
  };

  const updateFieldMapping = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      fieldMappings: prev.fieldMappings.map((mapping, i) =>
        i === index ? { ...mapping, [field]: value } : mapping
      )
    }));
  };

  const removeFieldMapping = (index) => {
    setConfig(prev => ({
      ...prev,
      fieldMappings: prev.fieldMappings.filter((_, i) => i !== index)
    }));
  };

  const fieldMappingSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Simple case (e.g. the AppendCSV "Field Mappings" list below) — every entry is
  // draggable, so this reorders the array directly by position.
  const handleFieldMappingDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setConfig(prev => {
      const oldIndex = prev.fieldMappings.findIndex((_, i) => i === active.id);
      const newIndex = prev.fieldMappings.findIndex((_, i) => i === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return { ...prev, fieldMappings: arrayMove(prev.fieldMappings, oldIndex, newIndex) };
    });
  };

  // Filtered case (the main "Field Mappings / Parameters" list, which hides
  // system-managed entries) — reorders only among the VISIBLE entries, leaving each
  // hidden entry exactly where it already was.
  const handleVisibleFieldMappingDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setConfig(prev => {
      const visible = prev.fieldMappings.filter(m => !isSystemManagedField(m));
      const oldIndex = visible.findIndex((_, i) => i === active.id);
      const newIndex = visible.findIndex((_, i) => i === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reorderedVisible = arrayMove(visible, oldIndex, newIndex);
      let cursor = 0;
      return {
        ...prev,
        fieldMappings: prev.fieldMappings.map(m =>
          isSystemManagedField(m) ? m : reorderedVisible[cursor++]
        ),
      };
    });
  };

  // Get steps that can receive field mappings (create and update steps, excluding current)
  const getFieldMappingTargetSteps = () => {
    if (!allNodes) return [];
    return allNodes.filter(n =>
      n.id !== node?.id &&
      (n.data?.stepType === 'create' || n.data?.stepType === 'update')
    );
  };

  // Copy field mappings to another step
  const copyFieldMappingsToStep = (targetNodeId) => {
    if (onCopyFieldMappings) {
      // Filter out system-managed fields when copying
      const mappingsToCopy = config.fieldMappings.filter(mapping => !isSystemManagedField(mapping));
      onCopyFieldMappings(targetNodeId, mappingsToCopy);
      setShowCopyFieldMappingsModal(false);
    }
  };

  const addFilter = () => {
    setConfig(prev => ({
      ...prev,
      filters: [...prev.filters, { field: '', operator: 'equals', value: '' }]
    }));
  };

  const updateFilter = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      filters: prev.filters.map((filter, i) =>
        i === index ? { ...filter, [field]: value } : filter
      )
    }));
  };

  const handleSaveAndClose = () => {
    // Auto-save changes when closing
    if (onUpdate) {
      onUpdate(node.id, config);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleCancel = () => {
    // Restore original config immediately via onUpdate (bypassing auto-save)
    if (originalConfig && onUpdate) {
      onUpdate(node.id, originalConfig);
    }

    // Close without saving (since we already restored directly)
    if (onClose) {
      onClose(true); // Pass true to indicate this is a cancel (don't save)
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewResult(null);
    setPreviewError(null);
    setPushDataUrlCopied(false);

    // The left-hand box holds the raw test payload as typed — it isn't parsed or
    // validated as JSON here, since pushed data doesn't have to be JSON-shaped. It's
    // sent verbatim as PreviewPushDataString; the runbook/client identifiers are
    // attached separately rather than being part of what the user edits.
    const requestBody = {
      urlRunbookId: runbookContext?.runbookId || '',
      urlClientId: runbookContext?.clientId || '',
      urlClientGuid: runbookContext?.clientGuid || '',
      urlRunbookGuid: runbookContext?.runbookGuid || '',
      PreviewPushDataString: previewData,
    };

    try {
      const response = await runbookService.preview(requestBody);

      const responseData = response.data;

      // The API wraps its actual payload in a `result` field that is itself a JSON-
      // encoded string (e.g. { result: '{"mode":"Raw","records":{...}}', pushDataUrl }),
      // rather than returning mode/records at the top level. Unwrap that one extra
      // layer of string-encoding, wherever it shows up, before reading mode/records.
      let inner = responseData;
      if (responseData && typeof responseData.result === 'string') {
        try {
          inner = JSON.parse(responseData.result);
        } catch {
          inner = responseData.result;
        }
      } else if (typeof responseData === 'string') {
        try {
          inner = JSON.parse(responseData);
        } catch {
          inner = responseData;
        }
      }

      // pushDataUrl rides alongside `result` at the top level, not inside it — merge it
      // back in so the rest of the UI can read mode/records/pushDataUrl off one object.
      const normalized =
        inner && typeof inner === 'object' && !Array.isArray(inner)
          ? { ...inner, pushDataUrl: responseData?.pushDataUrl ?? inner.pushDataUrl }
          : inner;

      setPreviewResult(normalized);
    } catch (err) {
      const msg = err?.response?.data || err?.message || 'Preview failed. Check that the data format is correct.';
      setPreviewError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Recursively extract flat fields and LineDataSetPointer tree from a records object.
  // Scans ALL records at each level to catch pointers that only appear in some rows.
  function extractPointerTree(records) {
    if (!records || typeof records !== 'object') return { flatFields: [], nestedArrays: [] };

    const flatFieldSet = new Set();
    // Map key → best non-empty sub-records found (for recursive parsing)
    const pointerData = new Map();

    Object.values(records).forEach(record => {
      if (!record || typeof record !== 'object') return;
      Object.entries(record).forEach(([key, value]) => {
        if (key.endsWith('LineDataSetPointer')) {
          const hasData = value && typeof value === 'object' && Object.keys(value).length > 0;
          if (hasData) {
            const existing = pointerData.get(key);
            if (!existing) {
              pointerData.set(key, { ...value });
            } else {
              // Merge records from all occurrences so deeply nested pointers aren't missed
              const offset = Object.keys(existing).length;
              Object.values(value).forEach((v, i) => { existing[offset + i] = v; });
            }
          } else if (!pointerData.has(key)) {
            pointerData.set(key, null); // register even if currently empty
          }
        } else {
          flatFieldSet.add(key);
        }
      });
    });

    const nestedArrays = Array.from(pointerData.entries()).map(([key, data]) => {
      const nested = data ? extractPointerTree(data) : { flatFields: [], nestedArrays: [] };
      return {
        key,
        arrayName: key.slice(0, -'LineDataSetPointer'.length),
        fields: nested.flatFields,        // flat fields visible at this nested level
        children: nested.nestedArrays,
      };
    });

    return { flatFields: Array.from(flatFieldSet), nestedArrays };
  }

  // Parse preview response into scaffold structure whenever previewResult changes
  useEffect(() => {
    if (!previewResult || typeof previewResult !== 'object' || !previewResult.records) {
      setScaffoldStructure(null);
      setScaffoldSelections(new Set());
      return;
    }
    const structure = extractPointerTree(previewResult.records);
    setScaffoldStructure(structure);

    // Auto-select all keys at every depth
    const allKeys = new Set();
    function collectAllKeys(arrays) {
      arrays.forEach(a => {
        allKeys.add(a.key);
        if (a.children?.length) collectAllKeys(a.children);
      });
    }
    collectAllKeys(structure.nestedArrays);
    setScaffoldSelections(allKeys);
  }, [previewResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScaffoldAddToCanvas = () => {
    if (!onScaffoldFromPreview || !scaffoldStructure) return;

    // Build the selected subtree — only include items the user ticked
    function buildSelectedTree(arrays) {
      return arrays
        .filter(p => scaffoldSelections.has(p.key))
        .map(p => ({
          key: p.key,
          arrayName: p.arrayName,
          fields: p.fields || [],
          children: buildSelectedTree(p.children || []),
        }));
    }

    const selectedTree = buildSelectedTree(scaffoldStructure.nestedArrays);

    // rootFields: all fields visible at the root forEach level (flat fields + top-level pointer keys)
    const rootFields = [
      ...(scaffoldStructure.flatFields || []),
      ...(scaffoldStructure.nestedArrays || []).map(n => n.key),
    ];

    // Nothing selected and nothing at the root to log either — genuinely nothing to build.
    if (!selectedTree.length && !rootFields.length) return;

    onScaffoldFromPreview({
      selectedTree,
      rootStepId: node?.data?.stepId || '',
      rootFields,
    });
    setShowPreviewModal(false);
  };

  const handleCopyPushDataUrl = () => {
    if (!previewResult?.pushDataUrl) return;
    navigator.clipboard.writeText(previewResult.pushDataUrl).then(() => {
      setPushDataUrlCopied(true);
      setTimeout(() => setPushDataUrlCopied(false), 2000);
    });
  };

  // Seed the template form values for a request type: context-derived defaults
  // first, then any values already present in the step's field mappings
  const buildPunchOutValues = (requestType) => {
    const values = {
      'Callback.ClientId': `${runbookContext?.clientId ?? ''}`,
      'Callback.ClientGuid': `${runbookContext?.clientGuid ?? ''}`,
    };
    (punchOutTemplates[requestType] || []).forEach(name => {
      const existing = config.fieldMappings.find(m => m.name === name);
      if (existing && existing.value !== '') {
        values[name] = existing.value;
      } else if (!(name in values)) {
        values[name] = '';
      }
    });
    return values;
  };

  const openPunchOutTemplateModal = () => {
    // If the step was already configured from a template, reopen with the
    // request type preselected and previously entered values populated
    const existingType = punchOutRequestTypes.includes(config.systemObject) ? config.systemObject : '';
    setPunchOutRequestType(existingType);
    setPunchOutTemplateValues(existingType ? buildPunchOutValues(existingType) : {});
    setPunchOutSampleText('');
    setPunchOutParseFeedback(null);
    setShowPunchOutTemplateModal(true);
  };

  // Parse a pasted sample request and prefill the template form from it
  const handleParsePunchOutSample = () => {
    const result = parsePunchOutSample(punchOutSampleText, punchOutRequestType);
    if (result.error) {
      setPunchOutParseFeedback({ variant: 'danger', message: result.error });
      return;
    }
    const templateFields = punchOutTemplates[punchOutRequestType] || [];
    const parsed = {};
    templateFields.forEach(name => {
      if (name in result.values) parsed[name] = result.values[name];
    });
    // Derive the runbook GUID when the parsed runbook id matches one of the
    // client's runbooks; otherwise quietly leave the picker for manual
    // selection — samples typically come from another environment, so a
    // non-matching runbook id is expected
    if (parsed['Callback.RunbookId'] !== undefined) {
      const matchedRunbook = availableRunbooks.find(r => `${r.runbookId}` === `${parsed['Callback.RunbookId']}`);
      if (matchedRunbook) {
        parsed['Callback.RunbookGuid'] = matchedRunbook.runbookGuid || '';
      } else {
        delete parsed['Callback.RunbookId'];
      }
    }
    const filledCount = Object.keys(parsed).length;
    // Fields never expected from the sample: Callback values derive from
    // context, the runbook picker, or manual entry; ItemOut is a raw XML
    // string entered manually
    const notExpectedFromSample = ['Callback.ClientId', 'Callback.ClientGuid', 'Callback.RunbookId', 'Callback.RunbookGuid', 'Callback.AccessToken', 'ItemOut'];
    const notFound = templateFields.filter(name => !(name in parsed) && !notExpectedFromSample.includes(name));
    setPunchOutTemplateValues(prev => ({ ...prev, ...parsed }));
    setPunchOutParseFeedback({
      variant: 'success',
      message: `Filled ${filledCount} field${filledCount === 1 ? '' : 's'} from the sample.`
        + (notFound.length ? ` Not found in the sample: ${notFound.join(', ')}.` : ''),
    });
  };

  // Add every field from the selected PunchOut template as a field mapping,
  // including blank values. Existing mappings with the same name are updated
  // instead of duplicated.
  const applyPunchOutTemplate = () => {
    const templateFields = punchOutTemplates[punchOutRequestType] || [];
    setConfig(prev => {
      const fieldMappings = [...prev.fieldMappings];
      templateFields.forEach(name => {
        const value = punchOutTemplateValues[name] || '';
        const existingIndex = fieldMappings.findIndex(m => m.name === name);
        if (existingIndex >= 0) {
          fieldMappings[existingIndex] = { ...fieldMappings[existingIndex], value };
        } else {
          fieldMappings.push({ name, value });
        }
      });
      // The selected request becomes the step's system object (e.g. PunchOutSetupRequest)
      return { ...prev, systemObject: punchOutRequestType, fieldMappings };
    });
    setShowPunchOutTemplateModal(false);
  };

  const handleConnectorSelect = (connectorId) => {
    const connector = connectors.find(c => c.connectorId === connectorId);
    if (connector) {
      const isSendGrid = connector.system === 'SendGrid';
      const sendGridFields = ['To', 'From', 'Subject', 'Body', 'BodyHtml', 'FromName', 'ReplyTo', 'Cc', 'Bcc', 'Attachments'];
      setConfig(prev => ({
        ...prev,
        connectorId: connector.connectorId,
        connectorNumber: connector.connectorNumber,
        system: connector.system,
        ...(isSendGrid && node.data.stepType === 'create' ? {
          systemObject: 'SendEmail',
          filter: '',
          fieldMappings: sendGridFields.map(name => ({
            name,
            value: prev.fieldMappings.find(m => m.name === name)?.value || ''
          }))
        } : {})
      }));
      // PunchOut connectors get a template form to pre-add field mappings
      // (case-insensitive match to tolerate system name variants)
      if (connector.system?.toLowerCase().includes('punchout') && !readOnly) {
        openPunchOutTemplateModal();
      }
    }
  };

  if (!node) return null;

  return (
    <>
    <Card className="h-100">
      <Card.Header className="bg-gradient-primary text-white d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          {config.system && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                borderRadius: 6,
                background: '#ffffff',
                flex: '0 0 auto',
              }}
            >
              <SystemIcon
                system={config.system}
                iconUrl={runbookSystemReducer.response?.find((s) => s.system === config.system)?.iconUrl}
                size={24}
                className="text-secondary"
              />
            </span>
          )}
          <div>
            <h6 className="mb-0">Configure Step</h6>
            <small className="opacity-75">{node.data.stepType}</small>
          </div>
        </div>
        <Button variant="link" className="text-white p-0" onClick={handleSaveAndClose} title="Save and close">
          <FaTimes />
        </Button>
      </Card.Header>
      <Card.Body style={{ maxHeight: '700px', overflowY: 'auto' }}>
        {readOnly && (
          <div className="alert alert-info py-2 mb-3" role="alert">
            <small><strong>Read-Only:</strong> Viewing step configuration</small>
          </div>
        )}
        <Form onSubmit={(e) => e.preventDefault()} style={readOnly ? { pointerEvents: 'none', opacity: 0.7 } : {}}>
          {/* Basic Info */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">Label</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={config.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Step label"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={config.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Step description"
            />
          </Form.Group>

          {/* ForEach-specific fields */}
          {node.data.stepType === 'forEach' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Data Step ID</Form.Label>
                <Form.Select
                  size="sm"
                  value={config.dataStepId}
                  onChange={(e) => handleChange('dataStepId', e.target.value)}
                >
                  <option value="">Auto-detect from previous step</option>
                  {allNodes && allNodes
                    .filter(n => n.id !== 'start' && n.type !== 'groupNode' && n.id !== node.id)
                    .map(n => (
                      <option key={n.id} value={n.data.stepId || n.id}>
                        {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                      </option>
                    ))
                  }
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  The step that provides the data to loop over. Auto-detects if previous step is a query or queryTableCache.
                  Description will automatically use the source step's System Object.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  size="sm"
                  label="Offset Record Processing"
                  checked={config.offsetRecordProcessing}
                  onChange={(e) => handleChange('offsetRecordProcessing', e.target.checked)}
                />
              </Form.Group>

              {config.offsetRecordProcessing && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Offset (milliseconds)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={config.offset}
                    onChange={(e) => handleChange('offset', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Delay in milliseconds between processing each record
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  size="sm"
                  label="Debug Mode"
                  checked={config.debugMode}
                  onChange={(e) => handleChange('debugMode', e.target.checked)}
                />
              </Form.Group>

              {config.debugMode && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Debug Starting At</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={config.debugStartingAt}
                      onChange={(e) => handleChange('debugStartingAt', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Start debugging at this record index
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Debug Number of Records</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={config.debugNumberOfRecords}
                      onChange={(e) => handleChange('debugNumberOfRecords', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Number of records to process in debug mode
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  size="sm"
                  label="Serial Processing"
                  checked={config.serialProcessing}
                  onChange={(e) => handleChange('serialProcessing', e.target.checked)}
                />
                <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                  Process records one at a time instead of in parallel
                </Form.Text>
              </div>
            </>
          )}

          {/* Query-specific fields */}
          {(node.data.stepType === 'query' || node.data.stepType === 'lookup' ||
            node.data.stepType === 'create' || node.data.stepType === 'update' ||
            node.data.stepType === 'delete' || node.data.stepType === 'executeStoredProcedure') && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  Connector <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={config.connectorId}
                  onChange={(e) => handleConnectorSelect(e.target.value)}
                >
                  <option value="">Select connector...</option>
                  {connectors && connectors.map(connector => (
                    <option key={connector.connectorId} value={connector.connectorId}>
                      {connector.connectorNumber} - {connector.system} - {connector.description}
                    </option>
                  ))}
                </Form.Select>
                {config.connectorId && (
                  <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                    ID: {config.connectorId} | Number: {config.connectorNumber}
                  </Form.Text>
                )}
              </Form.Group>

              {/* System field is hidden - automatically populated from connector selection */}
              {/* The system value is still saved in config.system via handleConnectorSelect */}

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  System Object <span className="text-danger">*</span>
                </Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    ref={systemObjectInputRef}
                    type="text"
                    size="sm"
                    value={config.systemObject}
                    onChange={handleSystemObjectChange}
                    onFocus={handleSystemObjectFocus}
                    onBlur={handleSystemObjectBlur}
                    onKeyDown={handleSystemObjectKeyDown}
                    placeholder="e.g., Account, Contact, Invoice"
                    autoComplete="off"
                    disabled={config.system === 'SendGrid' && node.data.stepType === 'create'}
                  />
                  {showSystemObjectSuggestions && systemObjectSuggestions.length > 0 && (
                    <ListGroup
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      {systemObjectSuggestions.map((suggestion, index) => (
                        <ListGroup.Item
                          key={suggestion.object}
                          active={index === selectedSuggestionIndex}
                          onClick={() => selectSystemObjectSuggestion(suggestion)}
                          style={{ cursor: 'pointer', padding: '8px 12px' }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {suggestion.object}
                          </div>
                          {suggestion.hint && (
                            <div style={{ fontSize: '0.75rem', color: index === selectedSuggestionIndex ? '#e0e0e0' : '#6c757d', marginTop: '2px' }}>
                              {suggestion.hint}
                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  The system object to query (supports dynamic values via FunctionParser)
                </Form.Text>
              </Form.Group>

              {!(config.system === 'SendGrid' && node.data.stepType === 'create') && (
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Fields to Query</Form.Label>
                <WorkflowAutocompleteWithExpand
                  value={config.fieldsToQuery}
                  onChange={(e) => handleChange('fieldsToQuery', e.target.value)}
                  allNodes={allNodes}
                  currentNode={node}
                  edges={edges}
                  runbookVariables={runbookVariables}
                  placeholder="e.g., Id,AccountId,Name,StageName or use {{ for steps, FX_ for functions"
                  className="form-control form-control-sm"
                  label="Fields to Query"
                  readOnly={readOnly}
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Comma-separated list of fields to return. Type FX_ for functions, {'{{'} for step references.
                </Form.Text>
              </Form.Group>
              )}

              {/* Hide Filter and Condition for executeStoredProcedure and SendGrid create */}
              {node.data.stepType !== 'executeStoredProcedure' && !(config.system === 'SendGrid' && node.data.stepType === 'create') && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Filter</Form.Label>
                    <WorkflowAutocompleteWithExpand
                      value={config.filter}
                      onChange={(e) => handleChange('filter', e.target.value)}
                      allNodes={allNodes}
                      currentNode={node}
                      edges={edges}
                      runbookVariables={runbookVariables}
                      placeholder="e.g., StageName = 'Closed Won' or use {{ for steps, FX_ for functions"
                      className="form-control form-control-sm"
                      label="Filter"
                      readOnly={readOnly}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      SOQL/query filter criteria. Type FX_ for functions, {'{{'} for step references.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Condition (Child ID Filter)</Form.Label>
                    <WorkflowAutocompleteWithExpand
                      value={config.condition}
                      onChange={(e) => handleChange('condition', e.target.value)}
                      allNodes={allNodes}
                      currentNode={node}
                      edges={edges}
                      runbookVariables={runbookVariables}
                      placeholder="e.g., @step1.AccountId or use {{ for steps, FX_ for functions"
                      className="form-control form-control-sm"
                      label="Condition"
                      readOnly={readOnly}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Optional child ID filter. Type FX_ for functions, {'{{'} for step references.
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              {/* SFTP-specific fields */}
              {config.system === 'SFTP' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Fixed Lengths</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={config.fieldMappings.find(m => m.name === 'FixedLengths')?.value || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setConfig(prev => ({
                          ...prev,
                          fieldMappings: [
                            ...prev.fieldMappings.filter(m => m.name !== 'FixedLengths'),
                            ...(value ? [{ name: 'FixedLengths', value }] : [])
                          ]
                        }));
                      }}
                      placeholder="e.g., 10,20,15,30"
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Comma-separated integers defining fixed-width field lengths
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Skip Lines</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={config.fieldMappings.find(m => m.name === 'SkipLines')?.value || '0'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setConfig(prev => ({
                          ...prev,
                          fieldMappings: [
                            ...prev.fieldMappings.filter(m => m.name !== 'SkipLines'),
                            { name: 'SkipLines', value: value || '0' }
                          ]
                        }));
                      }}
                      placeholder="0"
                      min="0"
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Number of lines to skip at the beginning of the file
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              {/* Customer Engagement-specific fields (only for Query) */}
              {node.data.stepType === 'query' && config.system === 'Customer Engagement' && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    size="sm"
                    label="Include formatted values"
                    checked={config.fieldMappings.find(m => m.name === 'IncludeFormattedValues')?.value === 'true'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setConfig(prev => ({
                        ...prev,
                        fieldMappings: [
                          ...prev.fieldMappings.filter(m => m.name !== 'IncludeFormattedValues'),
                          ...(isChecked ? [{ name: 'IncludeFormattedValues', value: 'true' }] : [])
                        ]
                      }));
                    }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Include formatted values in the query results
                  </Form.Text>
                </Form.Group>
              )}

              {/* Field Mappings / Parameters */}
              {config.system === 'SendGrid' && node.data.stepType === 'create' ? (
                <div className="mb-3">
                  <Form.Label className="small fw-bold mb-2">SendGrid Email Fields</Form.Label>
                  {config.fieldMappings.map((mapping, index) => (
                    <Form.Group key={mapping.name} className="mb-2">
                      <Form.Label className="small">{mapping.name}</Form.Label>
                      <WorkflowAutocompleteWithExpand
                        value={mapping.value}
                        onChange={(e) => updateFieldMapping(index, 'value', e.target.value)}
                        allNodes={allNodes}
                        currentNode={node}
                        edges={edges}
                        runbookVariables={runbookVariables}
                        placeholder={`Enter ${mapping.name} ({{ for steps, FX_ for functions)`}
                        className="form-control form-control-sm"
                        label={mapping.name}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  ))}
                </div>
              ) : (
              <div className="mb-3">
                <Form.Label className="small fw-bold mb-2">
                  {node.data.stepType === 'executeStoredProcedure' ? 'Parameters' : 'Field Mappings'}
                </Form.Label>
                {(() => {
                  const visibleMappings = config.fieldMappings.filter(m => !isSystemManagedField(m));
                  const isStoredProcedure = node.data.stepType === 'executeStoredProcedure';
                  const dragEnabled = !readOnly && visibleMappings.length > 1;
                  return (
                    <DndContext
                      sensors={fieldMappingSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleVisibleFieldMappingDragEnd}
                    >
                      <SortableContext
                        items={visibleMappings.map((_, i) => i)}
                        strategy={verticalListSortingStrategy}
                      >
                        {visibleMappings.map((mapping, index) => {
                          // Get the actual index in the original array for proper updates
                          const actualIndex = config.fieldMappings.indexOf(mapping);
                          return (
                            <SortableFieldMappingCard key={actualIndex} id={index} dragEnabled={dragEnabled}>
                              <Row className="g-2 align-items-center">
                                <Col xs={5}>
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder={isStoredProcedure ? "Parameter Name" : "Name"}
                                    value={mapping.name}
                                    onChange={(e) => updateFieldMapping(actualIndex, 'name', e.target.value)}
                                  />
                                </Col>
                                <Col xs={5}>
                                  <WorkflowAutocompleteWithExpand
                                    value={mapping.value}
                                    onChange={(e) => updateFieldMapping(actualIndex, 'value', e.target.value)}
                                    allNodes={allNodes}
                                    currentNode={node}
                                    edges={edges}
                                    runbookVariables={runbookVariables}
                                    placeholder="Value ({{ for steps, FX_ for functions)"
                                    className="form-control form-control-sm"
                                    label={isStoredProcedure ? `Parameter - ${mapping.name || 'Value'}` : `Field Mapping - ${mapping.name || 'Value'}`}
                                    readOnly={readOnly}
                                  />
                                </Col>
                                {!readOnly && (
                                  <Col xs={2} className="text-end">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="text-danger p-0"
                                      onClick={() => removeFieldMapping(actualIndex)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </Col>
                                )}
                              </Row>
                            </SortableFieldMappingCard>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  );
                })()}
                {!readOnly && (
                  <div className="d-flex gap-2 mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={addFieldMapping}>
                      <FaPlus className="me-1" /> {node.data.stepType === 'executeStoredProcedure' ? 'Add Parameter' : 'Add Field Mapping'}
                    </Button>
                    {config.system?.toLowerCase().includes('punchout') && (
                      <Button variant="outline-primary" size="sm" onClick={openPunchOutTemplateModal}>
                        <FaPlus className="me-1" /> Template
                      </Button>
                    )}
                    {node.data.stepType !== 'executeStoredProcedure' && config.fieldMappings.filter(m => !isSystemManagedField(m)).length > 0 && getFieldMappingTargetSteps().length > 0 && (
                      <Button variant="outline-primary" size="sm" onClick={() => setShowCopyFieldMappingsModal(true)}>
                        <FaCopy className="me-1" /> Copy To...
                      </Button>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Error Handling - hidden for SendGrid create */}
              {!(config.system === 'SendGrid' && node.data.stepType === 'create') && (
              <div className="mb-3">
                <Form.Label className="small fw-bold">Error Handling</Form.Label>
                <Form.Group className="mb-2">
                  <WorkflowAutocompleteWithExpand
                    value={config.customErrorMessage}
                    onChange={(e) => handleChange('customErrorMessage', e.target.value)}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Custom error message ({{ for steps, FX_ for functions)"
                    className="form-control form-control-sm"
                    label="Error Handling"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Type FX_ for functions, {'{{'} for step references.
                  </Form.Text>
                </Form.Group>
              </div>
              )}

              {/* Options - Hide Net Change for executeStoredProcedure and SendGrid create */}
              {node.data.stepType !== 'executeStoredProcedure' && !(config.system === 'SendGrid' && node.data.stepType === 'create') && (
                <div className="mb-3">
                  <Form.Check
                    type="checkbox"
                    size="sm"
                    label="Net Change"
                    checked={config.netChange}
                    onChange={(e) => handleChange('netChange', e.target.checked)}
                  />
                </div>
              )}

              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  size="sm"
                  label="Inspect Request"
                  checked={config.inspectRequest}
                  onChange={(e) => handleChange('inspectRequest', e.target.checked)}
                />
                <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                  Log detailed request information for debugging
                </Form.Text>
              </div>
            </>
          )}

          {/* BuildTableCache-specific fields */}
          {node.data.stepType === 'buildTableCache' && (() => {
            // Use node.data.dataStepId as fallback if config doesn't have it yet
            const currentDataStepId = config.dataStepId || node.data.dataStepId || '';

            // Find the selected data step (Query only)
            const selectedDataStep = currentDataStepId && allNodes
              ? allNodes.find(n => (n.data.stepId || n.id) === currentDataStepId)
              : null;

            // Get the fields to query from the selected data step
            const fieldsToQuery = selectedDataStep?.data?.fieldsToQuery || '';

            // Parse fields (comma-separated, removing whitespace)
            const fieldOptions = fieldsToQuery
              ? fieldsToQuery.split(',').map(f => f.trim()).filter(f => f && !f.startsWith('{{'))
              : [];

            // Get current key value from fieldMappings (check both 'name' and 'FieldName' for compatibility)
            const currentKeyMapping = config.fieldMappings?.find(m => m.name === 'key' || m.FieldName === 'key');
            const currentKeyValue = currentKeyMapping?.value || '';

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Data Step <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={currentDataStepId}
                    onChange={(e) => {
                      handleChange('dataStepId', e.target.value);
                      // Clear the key selection when data step changes (filter both 'name' and 'FieldName')
                      setConfig(prev => ({
                        ...prev,
                        dataStepId: e.target.value,
                        fieldMappings: prev.fieldMappings.filter(m => m.name !== 'key' && m.FieldName !== 'key')
                      }));
                    }}
                  >
                    <option value="">Select query step...</option>
                    {allNodes && allNodes
                      .filter(n => {
                        // Must be a query or getPushedData step
                        if (n.data.stepType !== 'query' && n.data.stepType !== 'getPushedData') return false;

                        // Don't show the current node
                        if (n.id === node.id) return false;

                        // If stepOrder exists for both nodes, only show steps that come before
                        if (n.data.stepOrder != null && node.data.stepOrder != null) {
                          return n.data.stepOrder < node.data.stepOrder;
                        }

                        // Otherwise, show all matching steps
                        return true;
                      })
                      .map(n => (
                        <option key={n.id} value={n.data.stepId || n.id}>
                          {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                        </option>
                      ))
                    }
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The query or get pushed data step that provides the data to cache
                  </Form.Text>
                </Form.Group>

                {currentDataStepId && selectedDataStep && (
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">
                      Key <span className="text-danger">*</span>
                    </Form.Label>

                    {/* Composite Key Checkbox */}
                    <Form.Check
                      type="checkbox"
                      id="composite-key-toggle"
                      label="Composite key (multiple fields)"
                      checked={currentKeyValue && currentKeyValue.includes(',')}
                      onChange={(e) => {
                        const selectedFields = currentKeyValue ? currentKeyValue.split(',').map(f => f.trim()) : [];
                        if (e.target.checked) {
                          // Switching to composite - if we have a single field, select a second one
                          // to enable composite mode (user needs at least 2 fields checked)
                          if (selectedFields.length === 1 && fieldOptions.length > 1) {
                            // Find the next available field that isn't already selected
                            const nextField = fieldOptions.find(f => !selectedFields.includes(f));
                            if (nextField) {
                              const newKeyValue = [...selectedFields, nextField].join(',');
                              setConfig(prev => ({
                                ...prev,
                                fieldMappings: [
                                  ...prev.fieldMappings.filter(m => m.name !== 'key' && m.FieldName !== 'key'),
                                  { name: 'key', value: newKeyValue }
                                ]
                              }));
                            }
                          } else if (selectedFields.length === 0 && fieldOptions.length >= 2) {
                            // No field selected yet - select first two
                            const newKeyValue = fieldOptions.slice(0, 2).join(',');
                            setConfig(prev => ({
                              ...prev,
                              fieldMappings: [
                                ...prev.fieldMappings.filter(m => m.name !== 'key' && m.FieldName !== 'key'),
                                { name: 'key', value: newKeyValue }
                              ]
                            }));
                          }
                        } else {
                          // Switching to single key - keep only the first field
                          const firstField = selectedFields[0];
                          setConfig(prev => ({
                            ...prev,
                            fieldMappings: [
                              ...prev.fieldMappings.filter(m => m.name !== 'key' && m.FieldName !== 'key'),
                              ...(firstField ? [{ name: 'key', value: firstField }] : [])
                            ]
                          }));
                        }
                      }}
                      className="mb-2"
                    />

                    {/* Single Key Dropdown (shown when not composite) */}
                    {!(currentKeyValue && currentKeyValue.includes(',')) && (
                      <Form.Select
                        size="sm"
                        value={currentKeyValue}
                        onChange={(e) => {
                          const selectedField = e.target.value;
                          setConfig(prev => ({
                            ...prev,
                            fieldMappings: [
                              ...prev.fieldMappings.filter(m => m.name !== 'key' && m.FieldName !== 'key'),
                              ...(selectedField ? [{ name: 'key', value: selectedField }] : [])
                            ]
                          }));
                        }}
                      >
                        <option value="">Select key field...</option>
                        {fieldOptions.map(field => (
                          <option key={field} value={field}>
                            {field}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    {/* Multi-Select Checkboxes (shown when composite) */}
                    {currentKeyValue && currentKeyValue.includes(',') && (() => {
                      const selectedFields = currentKeyValue.split(',').map(f => f.trim());
                      return (
                        <>
                          <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {fieldOptions.map(field => {
                              const isChecked = selectedFields.includes(field);
                              return (
                                <Form.Check
                                  key={field}
                                  type="checkbox"
                                  id={`key-field-${field}`}
                                  label={field}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    let newSelectedFields;
                                    if (e.target.checked) {
                                      // Add field - maintain order from fieldOptions
                                      newSelectedFields = fieldOptions.filter(f =>
                                        f === field || selectedFields.includes(f)
                                      );
                                    } else {
                                      // Remove field
                                      newSelectedFields = selectedFields.filter(f => f !== field);
                                    }
                                    const newKeyValue = newSelectedFields.join(',');
                                    setConfig(prev => ({
                                      ...prev,
                                      fieldMappings: [
                                        ...prev.fieldMappings.filter(m => m.name !== 'key' && m.FieldName !== 'key'),
                                        ...(newKeyValue ? [{ name: 'key', value: newKeyValue }] : [])
                                      ]
                                    }));
                                  }}
                                  className="mb-1"
                                />
                              );
                            })}
                          </div>
                          <div className="mt-2">
                            <small className="text-primary fw-bold">Selected: </small>
                            <small className="text-muted">{currentKeyValue}</small>
                          </div>
                        </>
                      );
                    })()}

                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      {currentKeyValue && currentKeyValue.includes(',')
                        ? 'Select multiple fields to create a composite key (comma-separated)'
                        : 'The field to use as the key for caching'}
                    </Form.Text>
                  </Form.Group>
                )}

                {currentDataStepId && !selectedDataStep && (
                  <div className="alert alert-warning small mb-3">
                    Selected data step not found. Please select a valid query step.
                  </div>
                )}

                {currentDataStepId && selectedDataStep && fieldOptions.length === 0 && (
                  <div className="alert alert-info small mb-3">
                    The selected query step has no fields defined. Please add fields to the query's "Fields to Query" first.
                  </div>
                )}
              </>
            );
          })()}

          {/* SetVar-specific fields */}
          {node.data.stepType === 'setVar' && (() => {
            // Find the selected declareVar step
            const selectedDeclareVar = config.dataStepId && allNodes
              ? allNodes.find(n => (n.data.stepId || n.id) === config.dataStepId)
              : null;

            // Check if setVar and declareVar share the same parentStepId
            // Normalize null to empty string, then compare
            const setVarParent = node.data.parentStepId || '';
            const declareVarParent = selectedDeclareVar?.data?.parentStepId || '';
            const sameParentStepId = selectedDeclareVar && setVarParent === declareVarParent;

            // If same parentStepId, force setVarType to 'Append' and disable the dropdown
            const isTypeDisabled = sameParentStepId;
            const effectiveSetVarType = sameParentStepId ? 'Append' : config.setVarType;

            // Update config if needed
            if (sameParentStepId && config.setVarType !== 'Append') {
              handleChange('setVarType', 'Append');
            }

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Data Step <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={config.dataStepId}
                    onChange={(e) => handleChange('dataStepId', e.target.value)}
                  >
                    <option value="">Select data step...</option>
                    {allNodes && allNodes
                      .filter(n => {
                        // Must be a declareVar step
                        if (n.data.stepType !== 'declareVar') return false;

                        // Don't show the current node
                        if (n.id === node.id) return false;

                        // If stepOrder exists for both nodes, use it for comparison
                        if (n.data.stepOrder != null && node.data.stepOrder != null) {
                          return n.data.stepOrder < node.data.stepOrder;
                        }

                        // Otherwise, show all declareVar steps
                        return true;
                      })
                      .map(n => (
                        <option key={n.id} value={n.data.stepId || n.id}>
                          {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                        </option>
                      ))
                    }
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The declareVar step that will receive the value (only steps defined before this step)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Set Var Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={effectiveSetVarType}
                    onChange={(e) => handleChange('setVarType', e.target.value)}
                    disabled={isTypeDisabled}
                  >
                    <option value="">Select type...</option>
                    <option value="Append">Append</option>
                    <option value="Count">Count</option>
                    <option value="Sum">Sum</option>
                    <option value="MaxDate">MaxDate</option>
                    <option value="MinDate">MinDate</option>
                    <option value="FirstRec">FirstRec</option>
                    <option value="Unique">Unique</option>
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    {isTypeDisabled
                      ? 'Type is set to Append because setVar and declareVar share the same parent step'
                      : 'The type of value to set'
                    }
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Value <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={config.setVarValue}
                    onChange={(e) => handleChange('setVarValue', e.target.value)}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields"
                    className="form-control form-control-sm"
                    label="Set Variable Value"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Type {'{{'} to see available steps, then _ to see fields. Supports dynamic values via FunctionParser.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* JsonSetVar-specific fields */}
          {node.data.stepType === 'jsonSetVar' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  Data Step <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={config.dataStepId}
                  onChange={(e) => handleChange('dataStepId', e.target.value)}
                >
                  <option value="">Select data step...</option>
                  {allNodes && allNodes
                    .filter(n => {
                      if (n.data.stepType !== 'declareVar') return false;
                      if (n.id === node.id) return false;
                      if (n.data.stepOrder != null && node.data.stepOrder != null) {
                        return n.data.stepOrder < node.data.stepOrder;
                      }
                      return true;
                    })
                    .map(n => (
                      <option key={n.id} value={n.data.stepId || n.id}>
                        {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                      </option>
                    ))
                  }
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  The declareVar step that will receive the value (only steps defined before this step)
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Set Var Type</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value="AppendCSV"
                  disabled
                />
              </Form.Group>

              <div className="mb-3">
                <Form.Label className="small fw-bold mb-2">Field Mappings</Form.Label>
                <DndContext
                  sensors={fieldMappingSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleFieldMappingDragEnd}
                >
                  <SortableContext
                    items={config.fieldMappings.map((_, i) => i)}
                    strategy={verticalListSortingStrategy}
                  >
                    {config.fieldMappings.map((mapping, index) => (
                      <SortableFieldMappingCard
                        key={index}
                        id={index}
                        dragEnabled={!readOnly && config.fieldMappings.length > 1}
                      >
                        <Row className="g-2 align-items-center">
                          <Col xs={4}>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Name"
                              value={mapping.name}
                              onChange={(e) => updateFieldMapping(index, 'name', e.target.value)}
                            />
                          </Col>
                          <Col xs={3}>
                            <Form.Control
                              as="select"
                              size="sm"
                              value={mapping.fieldType || 'string'}
                              onChange={(e) => updateFieldMapping(index, 'fieldType', e.target.value)}
                            >
                              <option value="string">string</option>
                              <option value="int">int</option>
                              <option value="bool">bool</option>
                              <option value="decimal">decimal</option>
                              <option value="datetime">datetime</option>
                              <option value="json">json</option>
                            </Form.Control>
                          </Col>
                          <Col xs={4}>
                            <WorkflowAutocompleteWithExpand
                              value={mapping.value}
                              onChange={(e) => updateFieldMapping(index, 'value', e.target.value)}
                              allNodes={allNodes}
                              currentNode={node}
                              edges={edges}
                              runbookVariables={runbookVariables}
                              placeholder="Value ({{ for steps)"
                              className="form-control form-control-sm"
                              label={`Field Mapping - ${mapping.name || 'Value'}`}
                              readOnly={readOnly}
                            />
                          </Col>
                          {!readOnly && (
                            <Col xs={1} className="text-end">
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={() => removeFieldMapping(index)}
                              >
                                <FaTrash />
                              </Button>
                            </Col>
                          )}
                        </Row>
                      </SortableFieldMappingCard>
                    ))}
                  </SortableContext>
                </DndContext>
                {!readOnly && (
                  <Button variant="outline-secondary" size="sm" onClick={addFieldMapping}>
                    <FaPlus className="me-1" /> Add Field Mapping
                  </Button>
                )}
              </div>
            </>
          )}

          {/* LogMessage-specific fields */}
          {node.data.stepType === 'logMessage' && (() => {
            // Get current ValueToLog from fieldMappings
            const valueToLogMapping = config.fieldMappings?.find(m => m.name === 'ValueToLog');
            const currentValueToLog = valueToLogMapping?.value || '';

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Value To Log <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentValueToLog}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfig(prev => ({
                        ...prev,
                        fieldMappings: [
                          ...prev.fieldMappings.filter(m => m.name !== 'ValueToLog'),
                          ...(value ? [{ name: 'ValueToLog', value }] : [])
                        ]
                      }));
                    }}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields"
                    className="form-control form-control-sm"
                    label="Value To Log"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The value to log. Type {'{{'} to see available steps, then _ to see fields. Supports dynamic values via FunctionParser.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* UpsertClientVar-specific fields */}
          {node.data.stepType === 'upsertClientVar' && (() => {
            // Get current field mappings
            const variableNumberMapping = config.fieldMappings?.find(m => m.name === 'VariableNumber' || m.fieldName === 'VariableNumber');
            const keyMapping = config.fieldMappings?.find(m => m.name === 'Key' || m.fieldName === 'Key');
            const valueMapping = config.fieldMappings?.find(m => m.name === 'Value' || m.fieldName === 'Value');

            const currentVariableNumber = variableNumberMapping?.value || '';
            const currentKey = keyMapping?.value || '';
            const currentValue = valueMapping?.value || '';

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Variable <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={currentVariableNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfig(prev => ({
                        ...prev,
                        fieldMappings: [
                          ...prev.fieldMappings.filter(m => m.name !== 'VariableNumber' && m.fieldName !== 'VariableNumber'),
                          ...(value ? [{ name: 'VariableNumber', value }] : [])
                        ]
                      }));
                    }}
                  >
                    <option value="">Select variable...</option>
                    {clientVariables && clientVariables.map(v => (
                      <option key={v.variableNumber} value={v.variableNumber}>
                        {v.variableNumber} - {v.variableName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Select the client variable from My Variables to upsert
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Key <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentKey}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfig(prev => ({
                        ...prev,
                        fieldMappings: [
                          ...prev.fieldMappings.filter(m => m.name !== 'Key' && m.fieldName !== 'Key'),
                          ...(value ? [{ name: 'Key', value }] : [])
                        ]
                      }));
                    }}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields"
                    className="form-control form-control-sm"
                    label="Key"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The key for the variable. Type {'{{'} to see available steps, then _ to see fields.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Value <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfig(prev => ({
                        ...prev,
                        fieldMappings: [
                          ...prev.fieldMappings.filter(m => m.name !== 'Value' && m.fieldName !== 'Value'),
                          ...(value ? [{ name: 'Value', value }] : [])
                        ]
                      }));
                    }}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields"
                    className="form-control form-control-sm"
                    label="Value"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The value to store. Type {'{{'} to see available steps, then _ to see fields.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* LookupTableCache-specific fields */}
          {node.data.stepType === 'lookupTableCache' && (() => {
            // Use node.data.dataStepId as fallback if config doesn't have it yet
            const currentDataStepId = config.dataStepId || node.data.dataStepId || '';

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Data Step <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={currentDataStepId}
                    onChange={(e) => handleChange('dataStepId', e.target.value)}
                  >
                    <option value="">Select build table cache step...</option>
                    {allNodes && allNodes
                      .filter(n => {
                        // Must be a buildTableCache step
                        if (n.data.stepType !== 'buildTableCache') return false;

                        // Don't show the current node
                        if (n.id === node.id) return false;

                        // If stepOrder exists for both nodes, only show steps that come before
                        if (n.data.stepOrder != null && node.data.stepOrder != null) {
                          return n.data.stepOrder < node.data.stepOrder;
                        }

                        // Otherwise, show all buildTableCache steps
                        return true;
                      })
                      .map(n => (
                        <option key={n.id} value={n.data.stepId || n.id}>
                          {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                        </option>
                      ))
                    }
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The Build Table Cache step to lookup from
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Filter <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={config.filter}
                    onChange={(e) => handleChange('filter', e.target.value)}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields, FX_ for functions"
                    className="form-control form-control-sm"
                    label="Filter"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The filter to use for looking up the cached data. Type {'{{'} for step references, FX_ for functions.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* QueryTableCache-specific fields */}
          {node.data.stepType === 'queryTableCache' && (() => {
            // Use node.data.dataStepId as fallback if config doesn't have it yet
            const currentDataStepId = config.dataStepId || node.data.dataStepId || '';
            const isDistinctKeys = config.filter === 'DistinctKeys';

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Table Cache <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={currentDataStepId}
                    onChange={(e) => handleChange('dataStepId', e.target.value)}
                    disabled={readOnly}
                  >
                    <option value="">Select table cache...</option>
                    {allNodes && allNodes
                      .filter(n => {
                        // Must be a buildTableCache step
                        if (n.data.stepType !== 'buildTableCache') return false;

                        // Don't show the current node
                        if (n.id === node.id) return false;

                        // If stepOrder exists for both nodes, only show steps that come before
                        if (n.data.stepOrder != null && node.data.stepOrder != null) {
                          return n.data.stepOrder < node.data.stepOrder;
                        }

                        // Otherwise, show all buildTableCache steps
                        return true;
                      })
                      .map(n => (
                        <option key={n.id} value={n.data.stepId || n.id}>
                          {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                        </option>
                      ))
                    }
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Select the Build Table Cache step to query from
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="distinct-keys-toggle"
                    label="Distinct Keys"
                    checked={isDistinctKeys}
                    disabled={readOnly}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleChange('filter', 'DistinctKeys');
                      } else {
                        handleChange('filter', '');
                      }
                    }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    When checked, returns all distinct key values from the cache
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Filter
                  </Form.Label>
                  <div style={isDistinctKeys ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                    <WorkflowAutocompleteWithExpand
                      value={isDistinctKeys ? 'distinctkeys' : (config.filter || '')}
                      onChange={(e) => handleChange('filter', e.target.value)}
                      allNodes={allNodes}
                      currentNode={node}
                      edges={edges}
                      runbookVariables={runbookVariables}
                      placeholder="Type {{ to reference steps and fields, FX_ for functions"
                      className="form-control form-control-sm"
                      label="Filter"
                      readOnly={readOnly}
                    />
                  </div>
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The filter to use for querying the cached data. Type {'{{'} for step references, FX_ for functions.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* IfElse-specific fields */}
          {node.data.stepType === 'ifElse' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  Condition <span className="text-danger">*</span>
                </Form.Label>
                <WorkflowAutocompleteWithExpand
                  value={config.condition}
                  onChange={(e) => handleChange('condition', e.target.value)}
                  allNodes={allNodes}
                  currentNode={node}
                  edges={edges}
                  runbookVariables={runbookVariables}
                  placeholder="Type {{ to reference steps and fields, FX_ for functions"
                  className="form-control form-control-sm"
                  label="Condition"
                  readOnly={readOnly}
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  The condition to evaluate. Type {'{{'} for step references, FX_ for functions. Returns true or false.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  True Path <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={config.conditionTruePath}
                  onChange={(e) => handleChange('conditionTruePath', e.target.value)}
                >
                  <option value="">Select step to execute when true...</option>
                  {allNodes && allNodes
                    .filter(n => {
                      // Don't show start node, group nodes, or current node
                      if (n.id === 'start' || n.type === 'groupNode' || n.id === node.id) return false;

                      // Only show steps with the same parentStepId (including both being blank/null/undefined)
                      const nodeParent = node.data.parentStepId || '';
                      const candidateParent = n.data.parentStepId || '';
                      if (nodeParent !== candidateParent) return false;

                      // Only show steps that come after this if/else (stepOrder must be greater)
                      if (n.data.stepOrder != null && node.data.stepOrder != null) {
                        return n.data.stepOrder > node.data.stepOrder;
                      }

                      // If stepOrder doesn't exist, show all steps
                      return true;
                    })
                    .map(n => (
                      <option key={n.id} value={n.data.stepId || n.id}>
                        {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                      </option>
                    ))
                  }
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  The step to execute when the condition is true
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  False Path <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={config.conditionFalsePath}
                  onChange={(e) => handleChange('conditionFalsePath', e.target.value)}
                >
                  <option value="">Select step to execute when false...</option>
                  {allNodes && allNodes
                    .filter(n => {
                      // Don't show start node, group nodes, or current node
                      if (n.id === 'start' || n.type === 'groupNode' || n.id === node.id) return false;

                      // Only show steps with the same parentStepId (including both being blank/null/undefined)
                      const nodeParent = node.data.parentStepId || '';
                      const candidateParent = n.data.parentStepId || '';
                      if (nodeParent !== candidateParent) return false;

                      // Only show steps that come after this if/else (stepOrder must be greater)
                      if (n.data.stepOrder != null && node.data.stepOrder != null) {
                        return n.data.stepOrder > node.data.stepOrder;
                      }

                      // If stepOrder doesn't exist, show all steps
                      return true;
                    })
                    .map(n => (
                      <option key={n.id} value={n.data.stepId || n.id}>
                        {n.data.stepId || n.id} - {n.data.label}{n.data.description ? ` (${n.data.description})` : ''}
                      </option>
                    ))
                  }
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  The step to execute when the condition is false
                </Form.Text>
              </Form.Group>
            </>
          )}

          {/* SendEmail-specific fields */}
          {node.data.stepType === 'sendEmail' && (() => {
            const currentSubject = config.fieldMappings?.find(m => m.name === 'Subject')?.value || '';
            const currentToAddress = config.fieldMappings?.find(m => m.name === 'ToAddress')?.value || '';
            const currentMessageBody = config.fieldMappings?.find(m => m.name === 'MessageBody')?.value || '';
            const currentAttachmentText = config.fieldMappings?.find(m => m.name === 'AttachmentText')?.value || '';

            const makeEmailChangeHandler = (fieldName) => (e) => {
              const value = e.target.value;
              setConfig(prev => ({
                ...prev,
                fieldMappings: [
                  ...prev.fieldMappings.filter(m => m.name !== fieldName),
                  ...(value ? [{ name: fieldName, value }] : [])
                ]
              }));
            };

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Subject <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentSubject}
                    onChange={makeEmailChangeHandler('Subject')}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields, FX_ for functions"
                    className="form-control form-control-sm"
                    label="Subject"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The email subject line.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    To Address <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentToAddress}
                    onChange={makeEmailChangeHandler('ToAddress')}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields, or enter email address"
                    className="form-control form-control-sm"
                    label="To Address"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Email recipient(s). Separate multiple addresses with semicolons.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Message Body <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentMessageBody}
                    onChange={makeEmailChangeHandler('MessageBody')}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields, FX_ for functions"
                    className="form-control form-control-sm"
                    label="Message Body"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The body/content of the email. Type {'{{'} for step references, FX_ for functions. Supports HTML.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Attachment Text</Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentAttachmentText}
                    onChange={makeEmailChangeHandler('AttachmentText')}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields, FX_ for functions"
                    className="form-control form-control-sm"
                    label="Attachment Text"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Optional attachment content.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* SetError-specific fields */}
          {node.data.stepType === 'setError' && (() => {
            // Get current ErrorMessage from fieldMappings
            const errorMessageMapping = config.fieldMappings?.find(m => m.name === 'ErrorMessage');
            const currentErrorMessage = errorMessageMapping?.value || '';

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">
                    Error Message <span className="text-danger">*</span>
                  </Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={currentErrorMessage}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfig(prev => ({
                        ...prev,
                        fieldMappings: [
                          ...prev.fieldMappings.filter(m => m.name !== 'ErrorMessage'),
                          ...(value ? [{ name: 'ErrorMessage', value }] : [])
                        ]
                      }));
                    }}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="Type {{ to reference steps and fields, FX_ for functions"
                    className="form-control form-control-sm"
                    label="Error Message"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    The error message to set. Type {'{{'} for step references, FX_ for functions.
                  </Form.Text>
                </Form.Group>
              </>
            );
          })()}

          {/* GetPushedData-specific fields */}
          {node.data.stepType === 'getPushedData' && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  size="sm"
                  id="nestedItemsCheck"
                  label="Nested items"
                  checked={config.nestedItems}
                  onChange={(e) => {
                    handleChange('nestedItems', e.target.checked);
                    if (!e.target.checked) {
                      handleChange('filter', '');
                    }
                  }}
                />
                <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                  Check to obtain nested data from original push.
                </Form.Text>
              </Form.Group>

              {config.nestedItems && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Filter</Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={config.filter}
                    onChange={(e) => handleChange('filter', e.target.value)}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder="e.g., Status = 'Active' or use {{ for steps, FX_ for functions"
                    className="form-control form-control-sm"
                    label="Nested Items Filter"
                    readOnly={readOnly}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Reference the nested field name and append <code>LineDataSetPointer</code>. e.g. if the original nest was <code>RS2_LineItems</code>, enter <code>{'{{RS2_LineItemsLineDataSetPointer}}'}</code>
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Fields</Form.Label>
                <WorkflowAutocompleteWithExpand
                  value={config.fieldsToQuery}
                  onChange={(e) => handleChange('fieldsToQuery', e.target.value)}
                  allNodes={allNodes}
                  currentNode={node}
                  edges={edges}
                  runbookVariables={runbookVariables}
                  placeholder="e.g., Id,Name,Status,Amount"
                  className="form-control form-control-sm"
                  label="Fields"
                  readOnly={readOnly}
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Comma-separated list of fields from the pushed data. Used for downstream step references only — these fields will be available when referencing this step from later steps.
                </Form.Text>
              </Form.Group>

              <div className="mt-1 mb-3">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setPreviewData(JSON.stringify({ '0': {} }, null, 2));
                    setPreviewResult(null);
                    setPreviewError(null);
                    setShowPreviewModal(true);
                  }}
                >
                  Preview Pushed Data
                </Button>
              </div>
            </>
          )}

          {/* ExecuteRunbook-specific fields */}
          {node.data.stepType === 'executeRunbook' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  Target Runbook <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={config.targetRunbookId}
                  onChange={(e) => {
                    const selectedRunbookId = e.target.value;
                    const selectedRunbook = availableRunbooks.find(r => r.runbookId === selectedRunbookId);
                    setConfig(prev => ({
                      ...prev,
                      targetRunbookId: selectedRunbookId,
                      targetRunbookGuid: selectedRunbook?.runbookGuid || '',
                      // Also update field mappings for the API
                      fieldMappings: [
                        ...prev.fieldMappings.filter(m => m.name !== 'runbookid' && m.name !== 'runbookguid'),
                        ...(selectedRunbookId ? [
                          { name: 'runbookid', value: selectedRunbookId },
                          { name: 'runbookguid', value: selectedRunbook?.runbookGuid || '' }
                        ] : [])
                      ]
                    }));
                  }}
                  disabled={readOnly}
                >
                  <option value="">Select a runbook...</option>
                  {availableRunbooks.map(runbook => (
                    <option key={runbook.runbookId} value={runbook.runbookId}>
                      {runbook.runbookId} - {runbook.runbookName}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Select the runbook to execute. The runbook GUID will be set automatically.
                </Form.Text>
              </Form.Group>

              {config.targetRunbookId && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Runbook GUID</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={config.targetRunbookGuid}
                    disabled
                    readOnly
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Auto-populated from the selected runbook.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </Form>
      </Card.Body>
      <Card.Footer className="bg-white border-top">
        <div className="d-flex gap-2">
          {readOnly ? (
            <Button variant="secondary" size="sm" className="flex-grow-1" onClick={handleCancel}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="primary" size="sm" className="flex-grow-1" onClick={handleSaveAndClose}>
                Save & Close
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </Card.Footer>
    </Card>

    {/* Copy Field Mappings Modal */}
    <Modal show={showCopyFieldMappingsModal} onHide={() => setShowCopyFieldMappingsModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Copy Field Mappings To...</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Select a Create or Update step to copy field mappings to. This will add the mappings to the target step.
        </p>
        <ListGroup>
          {getFieldMappingTargetSteps().map(targetNode => (
            <ListGroup.Item
              key={targetNode.id}
              action
              onClick={() => copyFieldMappingsToStep(targetNode.id)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{targetNode.id}</strong>
                  <Badge bg={targetNode.data.stepType === 'create' ? 'success' : 'info'} className="ms-2 text-capitalize">
                    {targetNode.data.stepType}
                  </Badge>
                  {targetNode.data.label && (
                    <span className="ms-2">{targetNode.data.label}</span>
                  )}
                </div>
                {targetNode.data.systemObject && (
                  <small className="text-muted">{targetNode.data.systemObject}</small>
                )}
              </div>
              {targetNode.data.description && (
                <small className="text-muted d-block mt-1">{targetNode.data.description}</small>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
        {getFieldMappingTargetSteps().length === 0 && (
          <p className="text-muted text-center">No Create or Update steps available to copy to.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowCopyFieldMappingsModal(false)}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>

    {/* PunchOut Template Modal */}
    <Modal show={showPunchOutTemplateModal} onHide={() => setShowPunchOutTemplateModal(false)} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }} className="d-flex align-items-center gap-2">
          <PixelWizard size={32} title="Template wizard" />
          Template
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-bold">Request</Form.Label>
          <Form.Select
            size="sm"
            value={punchOutRequestType}
            onChange={(e) => {
              setPunchOutRequestType(e.target.value);
              setPunchOutTemplateValues(e.target.value ? buildPunchOutValues(e.target.value) : {});
              setPunchOutSampleText('');
              setPunchOutParseFeedback(null);
            }}
          >
            <option value="">Select a request...</option>
            {punchOutRequestTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Form.Select>
        </Form.Group>
        {punchOutRequestType && (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold mb-1">Paste sample request (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                size="sm"
                style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                placeholder="Paste a sample cXML request here and the fields below will be filled in automatically..."
                value={punchOutSampleText}
                onChange={(e) => setPunchOutSampleText(e.target.value)}
              />
              <div className="d-flex align-items-center gap-2 mt-2">
                <Button variant="outline-primary" size="sm" onClick={handleParsePunchOutSample} disabled={!punchOutSampleText.trim()}>
                  Fill Fields from Sample
                </Button>
              </div>
              {punchOutParseFeedback && (
                <Alert variant={punchOutParseFeedback.variant} className="py-1 px-2 mt-2 mb-0 small">
                  {punchOutParseFeedback.message}
                </Alert>
              )}
            </Form.Group>
            <p className="text-muted small mb-2">
              Enter values for the fields below. All fields will be added as field mappings, including blank ones.
            </p>
            {(punchOutTemplates[punchOutRequestType] || []).map(name => {
              // Hidden in the UI but still added as field mappings:
              // ClientId/ClientGuid are derived from context, RunbookGuid
              // from the runbook picker below
              if (['Callback.ClientId', 'Callback.ClientGuid', 'Callback.RunbookGuid'].includes(name)) return null;
              if (name === 'Callback.RunbookId') {
                return (
                  <Form.Group key={name} className="mb-2">
                    <Form.Label className="small mb-1">Callback Runbook</Form.Label>
                    <Form.Select
                      size="sm"
                      value={punchOutTemplateValues['Callback.RunbookId'] || ''}
                      onChange={(e) => {
                        const selectedRunbookId = e.target.value;
                        const selectedRunbook = availableRunbooks.find(r => `${r.runbookId}` === selectedRunbookId);
                        setPunchOutTemplateValues(prev => ({
                          ...prev,
                          'Callback.RunbookId': selectedRunbookId,
                          'Callback.RunbookGuid': selectedRunbook?.runbookGuid || '',
                        }));
                      }}
                    >
                      <option value="">Select a runbook...</option>
                      {availableRunbooks.map(runbook => (
                        <option key={runbook.runbookId} value={runbook.runbookId}>
                          {runbook.runbookId} - {runbook.runbookName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Sets Callback.RunbookId and Callback.RunbookGuid automatically.
                    </Form.Text>
                  </Form.Group>
                );
              }
              return (
                <Form.Group key={name} className="mb-2">
                  <Form.Label className="small mb-1">{name}</Form.Label>
                  <WorkflowAutocompleteWithExpand
                    value={punchOutTemplateValues[name] || ''}
                    onChange={(e) => setPunchOutTemplateValues(prev => ({ ...prev, [name]: e.target.value }))}
                    allNodes={allNodes}
                    currentNode={node}
                    edges={edges}
                    runbookVariables={runbookVariables}
                    placeholder={`Enter ${name} ({{ for steps, FX_ for functions)`}
                    className="form-control form-control-sm"
                    label={name}
                  />
                </Form.Group>
              );
            })}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={() => setShowPunchOutTemplateModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={applyPunchOutTemplate} disabled={!punchOutRequestType}>
          <FaPlus className="me-1" /> Add
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Preview Pushed Data modal */}
    <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>Preview Pushed Data</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          {/* Left: editable request */}
          <Col md={6} className="d-flex flex-column">
            <Form.Label className="small fw-bold mb-1">Pushed data</Form.Label>
            <Form.Control
              as="textarea"
              value={previewData}
              onChange={(e) => setPreviewData(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.78rem', height: '280px', resize: 'none' }}
              disabled={previewLoading}
              spellCheck={false}
            />
            <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
              Whatever you type here is sent verbatim as the pushed payload — it doesn't have to be valid
              JSON. The current runbook's identifiers are attached automatically.
            </Form.Text>
          </Col>

          {/* Right: response + scaffold */}
          <Col md={6} className="d-flex flex-column">
            <Form.Label className="small fw-bold mb-1">Response</Form.Label>
            {previewError ? (
              <div
                className="alert alert-danger py-2 small mb-0"
                style={{ fontFamily: 'monospace', fontSize: '0.78rem', overflowY: 'auto', height: '280px' }}
              >
                {previewError}
              </div>
            ) : previewLoading ? (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ height: '280px', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}
              >
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="small text-muted">Running preview…</span>
              </div>
            ) : previewResult !== null ? (
              <Form.Control
                as="textarea"
                readOnly
                value={(() => {
                  if (typeof previewResult === 'string') return previewResult;
                  // mode and pushDataUrl already have their own dedicated UI below —
                  // showing them again here would just be noise.
                  const { mode, pushDataUrl, ...rest } = previewResult;
                  return JSON.stringify(rest, null, 2);
                })()}
                style={{ fontFamily: 'monospace', fontSize: '0.78rem', height: '280px', resize: 'none', backgroundColor: 'var(--bg-secondary)' }}
                spellCheck={false}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ height: '280px', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}
              >
                <span className="small text-muted">Response will appear here after running the preview.</span>
              </div>
            )}

            {/* Raw mode — the backend couldn't parse this into structured records, so it
                built and returned the actual push URL instead. */}
            {previewResult?.mode?.toLowerCase?.() === 'raw' && previewResult?.pushDataUrl && (
              <div className="mt-3 pt-3 border-top">
                <div className="small fw-bold mb-2">Push Data URL</div>
                <div className="text-muted mb-2" style={{ fontSize: '0.72rem' }}>
                  This runbook accepts raw pushed data. Send data to this URL instead of expecting
                  structured records.
                </div>
                <Form.Control
                  as="textarea"
                  readOnly
                  value={previewResult.pushDataUrl}
                  style={{ fontFamily: 'monospace', fontSize: '0.72rem', height: '80px', resize: 'none', backgroundColor: 'var(--bg-secondary)' }}
                  spellCheck={false}
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="mt-2 w-100"
                  onClick={handleCopyPushDataUrl}
                >
                  {pushDataUrlCopied ? 'Copied!' : 'Copy URL'}
                </Button>
              </div>
            )}

            {/* Scaffold section — appears after any successful preview. Building out is
                available even with no nested arrays: it still scaffolds a root forEach +
                log step over the top-level fields, it just skips the nested-array part. */}
            {previewResult !== null && scaffoldStructure && (
              <div className="mt-3 pt-3 border-top">
                <div className="small fw-bold mb-2">Build scaffold steps</div>

                {scaffoldStructure.nestedArrays.length === 0 ? (
                  <div className="small text-muted mb-2">
                    No nested arrays detected — this will just scaffold a loop over the root records with a log step.
                  </div>
                ) : (
                  <>
                    <div className="text-muted mb-2" style={{ fontSize: '0.72rem' }}>
                      Select arrays to scaffold. Nested arrays are indented under their parent.
                    </div>
                    {/* Recursive tree renderer */}
                    {(function renderPointerTree(pointers, parentSelected, depth) {
                      return pointers.map(({ key, arrayName, children }) => {
                        const isSelected = scaffoldSelections.has(key) && parentSelected;
                        const isDisabled = !parentSelected;
                        return (
                          <div key={key}>
                            <div style={{ paddingLeft: depth * 16 }}>
                              <Form.Check
                                type="checkbox"
                                id={`scaffold-${key}`}
                                label={
                                  <span className="small">
                                    {arrayName}
                                    {depth > 0 && (
                                      <span className="text-muted ms-1" style={{ fontSize: '0.68rem' }}>
                                        (nested)
                                      </span>
                                    )}
                                  </span>
                                }
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={(e) => {
                                  setScaffoldSelections(prev => {
                                    const next = new Set(prev);
                                    if (e.target.checked) {
                                      next.add(key);
                                    } else {
                                      next.delete(key);
                                      // Uncheck all descendants
                                      const uncheckAll = (cs) => cs?.forEach(c => {
                                        next.delete(c.key);
                                        uncheckAll(c.children);
                                      });
                                      uncheckAll(children);
                                    }
                                    return next;
                                  });
                                }}
                                className="mb-1"
                              />
                            </div>
                            {children?.length > 0 && renderPointerTree(children, isSelected, depth + 1)}
                          </div>
                        );
                      });
                    })(scaffoldStructure.nestedArrays, true, 0)}
                  </>
                )}

                {scaffoldStructure.flatFields.length > 0 && (
                  <div className="text-muted mt-2" style={{ fontSize: '0.68rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    <span className="fw-semibold">Root fields (always logged):</span> {scaffoldStructure.flatFields.join(', ')}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="mt-3 w-100"
                  onClick={handleScaffoldAddToCanvas}
                  disabled={scaffoldSelections.size === 0 && scaffoldStructure.flatFields.length === 0}
                >
                  Add to Canvas
                </Button>
                <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.68rem' }}>
                  Adds: forEach → log root fields{scaffoldStructure.nestedArrays.length > 0 ? ' → nested get pushed data → end loop for each selected array' : ''} → end loop
                </Form.Text>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          size="sm"
          onClick={handlePreview}
          disabled={previewLoading}
        >
          {previewLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-1" />
              Previewing…
            </>
          ) : 'Run Preview'}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setShowPreviewModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}

export default StepConfigPanel;
